// Bygger kalenderfiler (ICS, RFC 5545) för samordningsmöten så att deltagarna
// kan lägga in mötet i Outlook, Google Kalender eller Apple Kalender.

export interface IcsAttendee {
  name?: string;
  role?: string;
  email: string;
}

export interface IcsMeeting {
  uid: string;
  sequence: number;
  date: string; // YYYY-MM-DD
  startTime?: string | null; // HH:MM
  endTime?: string | null; // HH:MM
  location?: string | null;
  isOnline?: boolean;
  meetingUrl?: string | null;
  summary: string;
  description?: string;
  organizerEmail?: string | null;
  organizerName?: string | null;
  attendees?: IcsAttendee[];
  method?: "REQUEST" | "CANCEL";
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Wall time i Europe/Stockholm → UTC-stämpel enligt ICS-format. */
const toUtcStamp = (date: string, time: string): string => {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  // Utgå från att värdena är UTC och justera med den faktiska offseten i Stockholm.
  const naive = Date.UTC(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0);
  const probe = new Date(naive);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Stockholm",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(
    fmt.formatToParts(probe).map((p) => [p.type, p.value])
  ) as Record<string, string>;
  const asLocal = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) === 24 ? 0 : Number(parts.hour),
    Number(parts.minute),
    Number(parts.second)
  );
  const offset = asLocal - naive;
  const utc = new Date(naive - offset);
  return (
    `${utc.getUTCFullYear()}${pad(utc.getUTCMonth() + 1)}${pad(utc.getUTCDate())}` +
    `T${pad(utc.getUTCHours())}${pad(utc.getUTCMinutes())}00Z`
  );
};

const escapeText = (value: string): string =>
  value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");

/** Radvikning enligt RFC 5545 (max 75 oktetter per rad). */
const fold = (line: string): string => {
  if (line.length <= 75) return line;
  const chunks: string[] = [line.slice(0, 75)];
  let rest = line.slice(75);
  while (rest.length > 74) {
    chunks.push(` ${rest.slice(0, 74)}`);
    rest = rest.slice(74);
  }
  if (rest.length) chunks.push(` ${rest}`);
  return chunks.join("\r\n");
};

const stampNow = (): string => {
  const now = new Date();
  return (
    `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}` +
    `T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`
  );
};

const addMinutes = (time: string, minutes: number): string => {
  const [hh, mm] = time.split(":").map(Number);
  const total = (hh || 0) * 60 + (mm || 0) + minutes;
  return `${pad(Math.floor((total % 1440) / 60))}:${pad(total % 60)}`;
};

export const buildIcs = (meeting: IcsMeeting): string => {
  const method = meeting.method ?? "REQUEST";
  const start = meeting.startTime?.slice(0, 5) || null;
  const end = meeting.endTime?.slice(0, 5) || (start ? addMinutes(start, 60) : null);

  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Byggarbetsmiljokontrollen//Samordningsmote//SV",
    "CALSCALE:GREGORIAN",
    `METHOD:${method}`,
    "BEGIN:VEVENT",
    `UID:${meeting.uid}`,
    `DTSTAMP:${stampNow()}`,
    `SEQUENCE:${meeting.sequence ?? 0}`,
    `STATUS:${method === "CANCEL" ? "CANCELLED" : "CONFIRMED"}`,
    `SUMMARY:${escapeText(meeting.summary)}`,
  ];

  if (start && end) {
    lines.push(`DTSTART:${toUtcStamp(meeting.date, start)}`);
    lines.push(`DTEND:${toUtcStamp(meeting.date, end)}`);
  } else {
    const [y, m, d] = meeting.date.split("-").map(Number);
    const next = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + 1));
    lines.push(`DTSTART;VALUE=DATE:${meeting.date.replace(/-/g, "")}`);
    lines.push(
      `DTEND;VALUE=DATE:${next.getUTCFullYear()}${pad(next.getUTCMonth() + 1)}${pad(next.getUTCDate())}`
    );
  }

  const place = meeting.isOnline
    ? meeting.meetingUrl || meeting.location || "Digitalt möte"
    : meeting.location || "";
  if (place) lines.push(`LOCATION:${escapeText(place)}`);
  if (meeting.meetingUrl) lines.push(`URL:${meeting.meetingUrl}`);
  if (meeting.description) lines.push(`DESCRIPTION:${escapeText(meeting.description)}`);

  if (meeting.organizerEmail) {
    const cn = meeting.organizerName ? `;CN=${escapeText(meeting.organizerName)}` : "";
    lines.push(`ORGANIZER${cn}:mailto:${meeting.organizerEmail}`);
  }

  for (const a of meeting.attendees ?? []) {
    if (!a.email) continue;
    const cn = a.name ? `;CN=${escapeText(a.name)}` : "";
    lines.push(
      `ATTENDEE${cn};ROLE=REQ-PARTICIPANT;PARTSTAT=NEEDS-ACTION;RSVP=TRUE:mailto:${a.email}`
    );
  }

  if (method !== "CANCEL") {
    lines.push(
      "BEGIN:VALARM",
      "TRIGGER:-PT30M",
      "ACTION:DISPLAY",
      "DESCRIPTION:Påminnelse: samordningsmöte",
      "END:VALARM"
    );
  }

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.map(fold).join("\r\n");
};

export const downloadIcs = (fileName: string, ics: string): void => {
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".ics") ? fileName : `${fileName}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
